import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import {REMOVE_BG_POST_FIX_NAME_WITHOUT_EXTENSION, REMOVED_BG_IMAGE_EXTENSION} from '@Libraries/image-item.library';
import {ItemLoadingProgressType, ItemLoadingReason} from '@PosterWhiteboard/items/item/item.types';
import {doesRemovedBackgroundExists, removeImageBackground, removeUserImageBackground, pollImageBackgroundRemovalStatus} from '@Utils/image.util';
import {isPremiumUser} from '@Utils/user.util';
import {startItemLoading, removeItemLoading} from '@Libraries/item-loading.library';

export interface RemoveImageItemBackgroundObject {
  isBackgroundRemoved: boolean;
  isBackgroundBeingRemoved: boolean;
  pollingKey?: string;
}

export class RemoveImageItemBackground extends CommonMethods {
  public isBackgroundRemoved = false;
  public isBackgroundBeingRemoved = false;
  public isBackgroundRemovalPollingInitialized = false;
  public pollingKey?: string;

  private item: ImageItem | ImageSlideItem;

  constructor(item: ImageItem | ImageSlideItem) {
    super();
    this.item = item;
  }

  public toObject(): RemoveImageItemBackgroundObject {
    return {
      isBackgroundRemoved: this.isBackgroundRemoved,
      isBackgroundBeingRemoved: this.isBackgroundBeingRemoved,
      pollingKey: this.pollingKey,
    };
  }

  public clearInProgressState(): void {
    this.isBackgroundBeingRemoved = false;
    this.isBackgroundRemovalPollingInitialized = false;
    this.pollingKey = undefined;
  }

  public async ensureRemovedBackgroundImageExistsIfNeeded(): Promise<void> {
    if (!this.item.isImageUploading() && this.item.hashedFilename && this.isBackgroundRemoved && !(await this.doesRemovedBackgroundExists())) {
      await removeImageBackground(this.item.hashedFilename);
    }
  }

  public async doesRemovedBackgroundExists(): Promise<boolean> {
    if (!this.item.hashedFilename) {
      return false;
    }

    return doesRemovedBackgroundExists(this.item.hashedFilename, this.item.imageSource);
  }

  public async removeBackground(): Promise<void> {
    if (this.isBackgroundBeingRemoved || !this.item.hashedFilename) {
      return;
    }

    this.isBackgroundBeingRemoved = true;
    this.isBackgroundRemovalPollingInitialized = true;
    this.item.page.poster.redux.updateReduxData();
    try {
      startItemLoading(this.item, {
        reason: ItemLoadingReason.AI_REMOVE_BACKGROUND,
        langTextKeys: ['pmwjs_removing_background_percentage', 'pmwjs_removing_bg_percentage'],
        progress: {
          type: ItemLoadingProgressType.FAKE,
          expectedTotalTime: 20,
        },
      });

      if (await this.doesRemovedBackgroundExists()) {
        if (this.isBackgroundRemoved) {
          return;
        }

        await this.item.enableRemoveBackgroundFlag();
        return;
      }

      this.pollingKey = (await removeUserImageBackground(this.item.hashedFilename)).pollingKey;
      await pollImageBackgroundRemovalStatus(this.pollingKey);

      if (!this.item.existsInPage()) {
        return;
      }

      getPosterUi().setImageBgRemovalCreditsCost({
        imageKey: this.item.hashedFilename,
        credits: 0,
      });
      void getPosterUi().refreshTeamCreditsInStore();

      await this.item.enableRemoveBackgroundFlag();
    } catch (e: any) {
      this.onRemoveBackgroundError(e);
    } finally {
      this.resetRemoveBackgroundState();
    }
  }

  public async resumeRemoveBackground(): Promise<void> {
    if (!this.pollingKey || !this.item.hashedFilename) {
      return;
    }

    try {
      await pollImageBackgroundRemovalStatus(this.pollingKey);

      if (!this.item.existsInPage()) {
        return;
      }

      getPosterUi().setImageBgRemovalCreditsCost({
        imageKey: this.item.hashedFilename,
        credits: 0,
      });
      void getPosterUi().refreshTeamCreditsInStore();

      await this.item.enableRemoveBackgroundFlag();
    } catch (e: any) {
      this.onRemoveBackgroundError(e);
    } finally {
      this.resetRemoveBackgroundState();
    }
  }

  public resumeBackgroundRemovalIfNeeded(): void {
    if (this.isBackgroundBeingRemoved && !this.isBackgroundRemoved && !this.isBackgroundRemovalPollingInitialized) {
      this.isBackgroundRemovalPollingInitialized = true;
      void this.resumeRemoveBackground();
    }
  }

  private onRemoveBackgroundError(e: any): void {
    if (!this.item.existsInPage()) {
      return;
    }

    console.error(e);

    if (e.code === 'unprocessable_image') {
      getPosterUi().openMessageModal({
        title: window.i18next.t('pmwjs_remove_image_bg_failed'),
        text: window.i18next.t('pmwjs_remove_bg_unprocessable_image'),
      });
      return;
    }

    if (e.code === 'insufficient_credits') {
      if (!isPremiumUser()) {
        void window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_VIDEO_BG_REMOVALS);
      } else {
        void this.onInsufficientCredits();
      }
      return;
    }

    getPosterUi().openMessageModal({
      title: window.i18next.t('pmwjs_remove_image_bg_failed'),
      text: window.i18next.t('pmwjs_remove_image_bg_failed_desc'),
    });
  }

  private resetRemoveBackgroundState(): void {
    if (!this.item.existsInPage()) {
      return;
    }

    this.clearInProgressState();
    removeItemLoading(this.item);
    this.item.page.poster.redux.updateReduxData();
  }

  private onInsufficientCredits(): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      const cb = async (): Promise<void> => {
        await this.removeBackground();
        void getPosterUi().refreshTeamCreditsInStore();
        resolve();
      };

      try {
        getPosterUi().triggerBuyMoreCredits({
          headingText: window.i18next.t('pmwjs_purchase_more_credits_continue_using_ai_bg_remover_ai_images_and_more'),
          modalTitle: window.i18next.t('pmwjs_youre_out_of_credits'),
          cb,
        });
      } catch (e) {
        console.error(e);
        reject();
      }
    });
  }

  // TODO: Remove this function please its full of code smell
  public async getBackgroundRemovedURLForCurrentImageURL(): Promise<string> {
    const url = await this.item.getImageUrl();
    const removeBGURL = url.replace(REMOVE_BG_POST_FIX_NAME_WITHOUT_EXTENSION, '');
    const splitted = removeBGURL.split('.');
    const fileNameSplitted = splitted[splitted.length - 2].split('_');
    splitted[splitted.length - 2] = `${fileNameSplitted[0]}${REMOVE_BG_POST_FIX_NAME_WITHOUT_EXTENSION}${fileNameSplitted[1] !== undefined ? `_${fileNameSplitted[1]}` : ''}`;
    splitted[splitted.length - 1] = REMOVED_BG_IMAGE_EXTENSION;
    return splitted.reduce((left, curr) => {
      return `${left}.${curr}`;
    });
  }
}
